import { KakiatoRecorder } from 'kakiato';
import { KakiatoPlayer } from 'kakiato/player';

const textarea = document.getElementById('editor') as HTMLTextAreaElement | null;
const startBtn = document.getElementById('start') as HTMLButtonElement | null;
const shareBtn = document.getElementById('share') as HTMLButtonElement | null;
const confirmShareBtn = document.getElementById('confirm-share') as HTMLButtonElement | null;
const cancelShareBtn = document.getElementById('cancel-share') as HTMLButtonElement | null;
const shareModal = document.getElementById('share-modal') as HTMLDivElement | null;
const playBtn = document.getElementById('play') as HTMLButtonElement | null;
const passwordInput = document.getElementById('password') as HTMLInputElement | null;
const inlinePlayerSection = document.getElementById('inline-player-section') as HTMLElement | null;
const inlinePlayerContainer = document.getElementById('inline-player') as HTMLElement | null;

let recorder: KakiatoRecorder | null = null;
let exportedJson: string | null = null;
let isRecording = false;
let inlinePlayer: KakiatoPlayer | null = null;
let isInlinePlaying = false;
let inlineEndTimer: number | null = null;
// Keep base JSON when resuming so we can merge sessions
let baseJsonForMerge: string | null = null;

function mergeHrefJson(prevJson: string, nextJson: string): string {
  try {
    const a = JSON.parse(prevJson) as any;
    const b = JSON.parse(nextJson) as any;
    const aEvents: any[] = Array.isArray(a?.events) ? a.events : [];
    const bEvents: any[] = Array.isArray(b?.events) ? b.events : [];
    const lastTime = aEvents.length > 0 ? (aEvents[aEvents.length - 1].time ?? 0) : 0;
    const merged = {
      version: a?.version ?? b?.version ?? '0.1',
      session: a?.session ?? b?.session,
      initial_text: a?.initial_text ?? '',
      events: [
        ...aEvents,
        ...bEvents.map((e) => ({ ...e, time: (e?.time ?? 0) + lastTime })),
      ],
    };
    return JSON.stringify(merged, null, 2);
  } catch (e) {
    console.error('Failed to merge HREF JSON:', e);
    // Fallback to nextJson to avoid blocking the UX
    return nextJson;
  }
}

function ensureEl<T extends HTMLElement>(el: T | null, id: string): T {
  if (!el) throw new Error(`Missing element: #${id}`);
  return el;
}

window.addEventListener('DOMContentLoaded', () => {
  const $textarea = ensureEl(textarea, 'editor');
  const $start = ensureEl(startBtn, 'start');
  const $share = ensureEl(shareBtn, 'share');
  const $confirmShare = ensureEl(confirmShareBtn, 'confirm-share');
  const $cancelShare = ensureEl(cancelShareBtn, 'cancel-share');
  const $shareModal = ensureEl(shareModal, 'share-modal');
  const $play = ensureEl(playBtn, 'play');
  const $password = ensureEl(passwordInput, 'password');
  const shareResult = document.getElementById('share-result') as HTMLDivElement | null;
  const $shareResult = ensureEl(shareResult, 'share-result');
  const shareUrlInput = document.getElementById('share-url') as HTMLInputElement | null;
  const $shareUrlInput = ensureEl(shareUrlInput, 'share-url');
  const privacyAck = document.getElementById('privacy-ack') as HTMLInputElement | null;
  const $privacyAck = ensureEl(privacyAck, 'privacy-ack');
  const copyShareBtn = document.getElementById('copy-share-url') as HTMLButtonElement | null;
  const $copyShareBtn = ensureEl(copyShareBtn, 'copy-share-url');
  const titleInput = document.getElementById('share-title-input') as HTMLInputElement | null;
  const $titleInput = ensureEl(titleInput, 'share-title-input');
  const $inlineSection = ensureEl(inlinePlayerSection, 'inline-player-section');
  const $inlineContainer = ensureEl(inlinePlayerContainer, 'inline-player');
  // Localize placeholders based on html[lang]
  const lang = document.documentElement.getAttribute('lang') || 'en';
  if (lang.startsWith('ja')) {
    $titleInput.placeholder = 'タイトル';
    $password.placeholder = '4文字以上';
  } else {
    $titleInput.placeholder = 'Title';
    $password.placeholder = 'At least 4 characters';
  }

  recorder = new KakiatoRecorder({ target: $textarea });

  $start.disabled = false;
  $share.disabled = true;
  $play.disabled = true;

  function setRecordIcon(recording: boolean) {
    const label = recording ? 'Stop' : ($textarea.value.trim().length > 0 ? 'Resume' : 'Record');
    $start.title = label;
    $start.setAttribute('aria-label', label);
    $start.innerHTML = recording
      ? '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="7" y="7" width="10" height="10"/></svg>'
      : '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="7"/></svg>';
    if (recording) {
      $start.classList.add('is-stop');
    } else {
      $start.classList.remove('is-stop');
    }
  }
  function setPlayIcon(playing: boolean) {
    const label = playing ? 'Pause' : 'Play';
    $play.title = label;
    $play.setAttribute('aria-label', label);
    $play.innerHTML = playing
      ? '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="7" y="6" width="4" height="12"/><rect x="13" y="6" width="4" height="12"/></svg>'
      : '<svg viewBox="0 0 24 24" aria-hidden="true"><polygon points="8,7 17,12 8,17"/></svg>';
    if (playing) {
      $play.classList.add('is-stop');
    } else {
      $play.classList.remove('is-stop');
    }
  }
  setRecordIcon(false);
  setPlayIcon(false);

  // Focus editor on load and place caret at end
  queueMicrotask(() => {
    const end = $textarea.value.length;
    $textarea.focus();
    try { $textarea.setSelectionRange(end, end); } catch {}
  });

  function monitorInlineEnd(): void {
    if (!inlinePlayer) return;
    if (inlineEndTimer) {
      clearInterval(inlineEndTimer);
      inlineEndTimer = null;
    }
    inlineEndTimer = window.setInterval(() => {
      if (!inlinePlayer) return;
      const st = inlinePlayer.getState();
      if (!st.isPlaying && st.duration > 0 && st.currentTime === 0) {
        isInlinePlaying = false;
        setPlayIcon(false);
        // Return to editor after playback ends
        $inlineSection.style.display = 'none';
        $textarea.style.display = '';
        clearInterval(inlineEndTimer!);
        inlineEndTimer = null;
      }
    }, 250);
  }

  $start.addEventListener('click', () => {
    if (!isRecording) {
      // Store previous JSON for merging when resuming
      baseJsonForMerge = exportedJson;
      exportedJson = null;
      $share.disabled = true;
      // Ensure recorder captures current text as initial_text
      $textarea.focus();
      const end = $textarea.value.length;
      $textarea.setSelectionRange(end, end);
      recorder!.start();
      isRecording = true;
      $play.disabled = true;
      setRecordIcon(true);
      // Ensure editor visible when recording
      $inlineSection.style.display = 'none';
      $textarea.style.display = '';
    } else {
      recorder!.stop();
      const newJson = recorder!.exportJSON();
      // When resuming, merge the previous session with the new one
      exportedJson = baseJsonForMerge ? mergeHrefJson(baseJsonForMerge, newJson) : newJson;
      baseJsonForMerge = null;
      isRecording = false;
      const hasText = $textarea.value.trim().length > 0;
      $share.disabled = !hasText;
      $play.disabled = !hasText;
      setRecordIcon(false);
      // hide inline player until user presses Play
      $inlineSection.style.display = 'none';
      $textarea.style.display = '';
      if (inlinePlayer) {
        inlinePlayer.pause();
        inlinePlayer = null; // force re-init with latest JSON on next play
      }
      isInlinePlaying = false;
      setPlayIcon(false);
    }
  });

  // (stop button removed – handled by start toggle)

  // Share flow via modal
  function openShareModal() {
    if (!exportedJson) return;
    if ($textarea.value.trim().length === 0) return;
    $password.value = '';
    $titleInput.value = '';
    $shareResult.style.display = 'none';
    $privacyAck.checked = false;
    $confirmShare.disabled = true;
    $shareModal.style.display = 'block';
    queueMicrotask(() => $password.focus());
  }

  async function confirmShare() {
    if (!exportedJson) return;
    const password = $password.value.trim();
    if (password.length < 4) {
      alert('Password must be at least 4 characters.');
      return;
    }
    if (!$privacyAck.checked) {
      alert('Please check the privacy notice to continue.');
      return;
    }
    const res = await fetch('/api/recordings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: exportedJson, password, title: $titleInput.value.trim() || undefined }),
    });
    if (!res.ok) {
      const text = await res.text();
      alert(`Failed to save: ${res.status} ${text}`);
      return;
    }
    interface RecordingSaveResponse { uid: string }
    const json = (await res.json()) as RecordingSaveResponse;
    const url = `${location.origin}/view.html?uid=${json.uid}`;
    // Show URL inside modal instead of alerting/closing
    $shareUrlInput.value = url;
    $shareResult.style.display = '';
    $confirmShare.disabled = true;
    // Try copying automatically; ignore errors
    try { await navigator.clipboard.writeText(url); } catch {}
  }

  function cancelShare() {
    $shareModal.style.display = 'none';
  }

  $share.addEventListener('click', openShareModal);
  $confirmShare.addEventListener('click', () => { void confirmShare(); });
  $cancelShare.addEventListener('click', cancelShare);
  $privacyAck.addEventListener('change', () => {
    $confirmShare.disabled = !$privacyAck.checked;
  });
  // close modal when clicking overlay
  $shareModal.addEventListener('click', (e) => {
    if (e.target === $shareModal) cancelShare();
  });
  $password.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void confirmShare();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelShare();
    }
  });

  $copyShareBtn.addEventListener('click', async () => {
    const url = $shareUrlInput.value;
    try {
      await navigator.clipboard.writeText(url);
      const prev = $copyShareBtn.innerHTML;
      $copyShareBtn.classList.add('copied');
      $copyShareBtn.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 16.2l-3.5-3.5a1 1 0 10-1.4 1.4l4.2 4.2a1 1 0 001.4 0l8.2-8.2a1 1 0 10-1.4-1.4L9 16.2z"/></svg>';
      $copyShareBtn.title = 'Copied!';
      $copyShareBtn.setAttribute('aria-label', 'Copied!');
      setTimeout(() => {
        $copyShareBtn.classList.remove('copied');
        $copyShareBtn.innerHTML = prev;
        $copyShareBtn.title = 'Copy URL';
        $copyShareBtn.setAttribute('aria-label', 'Copy URL');
      }, 1200);
    } catch {}
  });

  // Auto-start recording when typing starts on empty input only
  $textarea.addEventListener('beforeinput', () => {
    if (!isRecording && $textarea.value.length === 0) {
      exportedJson = null;
      $share.disabled = true;
      recorder!.start();
      isRecording = true;
      $play.disabled = true;
      setRecordIcon(true);
    }
  });

  // Update start button accessibility label while paused
  $textarea.addEventListener('input', () => {
    if (!isRecording) setRecordIcon(false);
  });

  $play.addEventListener('click', () => {
    if (!exportedJson) return;
    // Swap to inline viewer when playing
    $inlineSection.style.display = '';
    $textarea.style.display = 'none';
    if (!inlinePlayer) {
      $inlineContainer.innerHTML = '';
      inlinePlayer = new KakiatoPlayer({
        container: $inlineContainer,
        speed: 1.0,
        showSelection: true,
        styles: {
          fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial, "Noto Sans", "Apple Color Emoji", "Segoe UI Emoji", sans-serif',
          fontSize: '1rem',
          lineHeight: '1.6',
          padding: '1rem 0',
          border: 'none',
          background: '#fff',
          minHeight: '360px',
          whiteSpace: 'pre-wrap',
        },
      });
    }
    // Always (re)load latest JSON so appended recording replays with correct timing
    inlinePlayer.stop();
    inlinePlayer.loadJSON(exportedJson);
    const st = inlinePlayer.getState();
    if (!isInlinePlaying) {
      // If ended (engine resets currentTime to 0), reset to ensure index=0
      if (!st.isPlaying && st.duration > 0 && st.currentTime === 0) {
        inlinePlayer.stop();
      }
      inlinePlayer.play();
      isInlinePlaying = true;
      setPlayIcon(true);
      monitorInlineEnd();
    } else {
      inlinePlayer.pause();
      isInlinePlaying = false;
      setPlayIcon(false);
      if (inlineEndTimer) { clearInterval(inlineEndTimer); inlineEndTimer = null; }
      // Return to editor when paused
      $inlineSection.style.display = 'none';
      $textarea.style.display = '';
    }
  });
});
